import { Component, ElementRef, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BlockActionType, BlockNodeEnum } from '@core/enums';
import { ArtsaasErrorCode } from '@core/enums/artsaas-error-code.enum';
import { MediaInterface } from '@core/interfaces/media.interface';
import { MixedObject } from '@core/interfaces/mixed-object';
import { BlockAction, ViewPart } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { ApiReCaptchaService } from '@core/services/common/api-re-captcha.service';
import { SubSink } from '@core/utils/subsink';
import {
  ARTWORK_DATA_FORM_FIELD_PREFIX,
  fileToBase64,
  getMediaFieldName,
  isArtworkDataPart,
  isArtworksDataUploadSingleThumbnailContainer,
  isArtworksDataUploadSingleThumbnailContainerWithProgress,
  isArtworksDataUploadThumbnailsContainer,
  isPersonalDataUploadSingleThumbnailContainer,
  isPersonalDataUploadSingleThumbnailContainerWithProgress,
  isPersonalDataUploadThumbnailsContainer,
  nestObject,
  PERSONAL_DATA_FILES_FORM_FIELD,
  PERSONAL_DATA_MEDIA_FORM_FIELD,
} from '@modules/creator/components/special-block/forms/artwork-request-form/artwork-request-form.helpers';
import {
  ArtworkRequestFormService,
} from '@modules/creator/components/special-block/forms/artwork-request-form/artwork-request-form.service';
import {
  ARTWORK_DATA_FILE_INPUT_FORM_FIELD_SUFFIX,
  ARTWORK_REQUEST_ERRORS,
  ITEMS_COUNTER_SNIPPET,
  PROGRESSBAR_SNIPPET,
  THUMBNAIL_SNIPPET,
} from '@modules/creator/components/special-block/forms/artwork-request-form/artwork-request-form.vars';
import { FormService } from '@modules/creator/components/special-block/forms/services/form.service';
import { getLoaderHtmlBrowser } from '@modules/loaders/helpers/get-loader-html';
import { SnackBarMessageComponent } from '@shared/components/snack-bar-message.component';
import { SessionStorageService } from '@shared/services/storage';
import { EMPTY, of } from 'rxjs';
import { catchError } from 'rxjs/operators';


/**
 * BAZOWY SZKIELET FORMULARZA
 *
 * - div [viewPart = artworkRequestForm]
 * |-- nagłówek [h6]
 * |
 * |-- div [viewPart = personalDataFormPart]
 * ||--- div
 * |||---- first_name [input]
 * |||---- last_name [input]
 * ||--- div
 * |||---- email [input]
 * |||---- phone [input]
 * ||--- message [textarea]
 * |
 * |-- div
 * ||--- h6
 * ||--- paragraf
 * |
 * |-- div [viewPart = artworksDataFormPart] - ten div powielamy
 * ||--- div - lewa kolumna
 * |||---- label
 * ||||----- SNIPPET itemCounter - nr sekcji
 * ||||----- div [viewPart = artworksDataInputContainer] - tu zawiera sie input i podgląd uploadowanych plików
 * |||||------ div [viewPart = artworksDataUploadThumbnailsContainer] - kontener miniatur - do niego dodajemy kolejne po wysłaniu plików
 * ||||||------- div [viewPart = artworksDataUploadSingleThumbnailContainer] - kontener pojedynczej miniatury - jego powielamy
 * |||||||-------- div [viewPart = artworksDataUploadSingleThumbnailContainerWithProgress] - bezpośredni kontener miniatury z progressbarem
 * ||||||||--------- SNIPPET uploadedFileThumbnail - miniaturka pliku
 * ||||||||--------- SNIPPET uploadProgressbar - pasek postępu uploadu
 * |||||||-------- ikonka usuwania pliku
 * |||||------ INPUT[type=file] - input do uploadu
 * ||||----- [paragraf z tekstem]
 * ||
 * ||--- div - środkowa kolumna
 * |||---- artist [input]
 * |||---- year_of_creation [input]
 * |||---- title [input]
 * |||---- material [input]
 * |||---- div
 * ||||----- weight [input]
 * ||||----- weight_unit [input]
 * |||---- div
 * ||||----- dimension_x [input]
 * ||||----- dimension_y [input]
 * ||||----- dimension_z [input]
 * ||||----- dimension_unit [input]
 * |||---- signature [textarea]
 * |||---- description [textarea]
 * ||
 * ||--- div - prawa kolumna
 * |||---- div
 * ||||----- ikona usuwania sekcji
 * |||---- div
 * ||||----- suggested_price [input]
 * ||||----- suggested_price_unit [input]
 * |
 * |
 * |-- paragraf [button] - Dodaj kolejną sekcje
 * |
 * |-- paragraf [checkbox] - akceptuje zgody itp
 * |
 * |-- div
 * ||---- anuluj [button]
 * ||---- wyślij [button]
 *
 *
 */
@Component({
  selector: 'app-artwork-request-form',
  styles: `
    .artwork-data-form {
      counter-increment: artwork-data;
    }

    .counter-class {
      position: relative;
    }

    .counter-class::before {
      content: counter(artwork-data);
      position: relative;
    }

    form {
      position: relative;
    }

    .spinner-center {
      position: absolute !important;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .hide-close-btn [action-name="CLEAR"] {
      opacity: 0;
      pointer-events: none;
      visibility: hidden;
    }

    /* AT-8869: Wyślij — wyraźnie wyszarzone + nieklikalne podczas uploadu */
    [action-name="SUBMIT"].is-disabled,
    [action-name="SUBMIT"][disabled],
    [action-name="SUBMIT"][aria-disabled="true"] {
      opacity: 0.45 !important;
      filter: grayscale(0.4) brightness(0.65) !important;
      pointer-events: none !important;
      cursor: not-allowed !important;
    }

    [action-name="SUBMIT"].is-disabled .ql-editor,
    [action-name="SUBMIT"][disabled] .ql-editor,
    [action-name="SUBMIT"][aria-disabled="true"] .ql-editor {
      opacity: 0.45 !important;
      color: inherit !important;
    }

    .validation-info {
      width: 100%;
      font-family: sans-serif;
      text-align: center;
      margin-top: 10px;
      margin-bottom: 20px;
      letter-spacing: 0.5px;

      color: var(--mat-form-field-error-text-color, var(--mat-sys-error));
    }
  `,
  encapsulation: ViewEncapsulation.None,
  template: `
    <form [formGroup]="formGroup" [class]="specialBlock.uId">
      <div [class]="formBody.uId">
        @for (blk of formBody.children; track $index) {
          @if (!isArtworkDataPart(blk)) {
            <div app-block [item]="blk" [class]="blk.uId + ' personal-data-form'" [appClickableBlock]="blk"></div>
          } @else {
            @for (blkArtworkData of artworkDataPartArray; track $index) {
              @if (!removedIndexes.includes($index)) {
                <div [item]="blkArtworkData"
                     [class]="blkArtworkData.uId + ' artwork-data-form idx-' + $index"
                     [appClickableBlock]="blkArtworkData"
                     app-block
                ></div>
              }
            }
          }
        }
      </div>
    </form>
  `,
  providers: [FormService, ArtworkRequestFormService],
  standalone: false,
})
export class ArtworkRequestFormComponent implements OnInit, OnDestroy {
  @Input() specialBlock: Block;
  formBody: Block;
  formGroup = this._fs.formGroup;
  artworkDataPartTemplate: Block;
  artworkDataPartArray: Block[] = [];
  singleThumbnailPartTemplate: Block;
  singleThumbnailPartPersonalDataTemplate: Block;
  removedIndexes: number[] = [];
  private subSink = new SubSink();
  isSendingInProgress = false;
  private loaderHtml: string;
  private submitBtnText: string;
  private maxFileSize = 1024 * 1024 * 10;
  private maxVideoFileSize = 1024 * 1024 * 100;
  private isSubmitDisabled = false;
  private _mediaSendingInProgressCount = 0;

  get mediaSendingInProgressCount(): number {
    return this._mediaSendingInProgressCount;
  }

  set mediaSendingInProgressCount(value: number) {
    // Clamp — przy błędzie uploadu catchError+next potrafiło podwójnie robić --
    // i licznik schodził poniżej 0 (disabled nigdy się nie włączał).
    this._mediaSendingInProgressCount = Math.max(0, value);
    this.setSubmitBtnDisableStatus(this._mediaSendingInProgressCount > 0);
  }

  get maxArtworksCount(): number {
    return this.specialBlock.settings.maxArtworks || 10;
  }

  get minArtworksCount(): number {
    return this.specialBlock.settings.minArtworks || 0;
  }

  get artworksDataLength(): number {
    return this.artworkDataPartArray.filter((v, k) => !this.removedIndexes.includes(k)).length;
  }

  get allArtworksDataLength(): number {
    return this.artworkDataPartArray.length;
  }

  get maxArtworksCountReached(): boolean {
    return this.artworksDataLength >= this.maxArtworksCount;
  }

  constructor(private element: ElementRef,
              private _snackBar: MatSnackBar,
              private _fs: FormService,
              private apiReCaptchaService: ApiReCaptchaService,
              private artworkRequestFormService: ArtworkRequestFormService,
              private _buttonHandlerService: ButtonHandlerService,
              private storageService: SessionStorageService) {
  }

  // sprawdza czy dany blok jest sekcją typu "ARTWORK_DATA_FORM_PART"
  isArtworkDataPart(item: Block): boolean {
    return isArtworkDataPart(item);
  }

  setIsSendingInProgress(val: boolean) {
    if (val) {
      this.isSendingInProgress = val;
      this.replaceSubmitBtnTextWithLoader();
    } else {
      setTimeout(() => {
        this.isSendingInProgress = val;
        this.replaceSubmitBtnLoaderWithText();
      }, 500);
    }
  }

  async ngOnInit(): Promise<void> {
    this.formBody = this.specialBlock.children[0]; // pierwszy element z children jako ciało formularza

    this.loaderHtml = await getLoaderHtmlBrowser({color: 'currentColor', type: 'spinner'});
    this.findAndAssignTemplateParts(this.formBody);
    // oprozniamy kontener miniatur z placeholderow
    this.removeSingleThumbnailContainerFromArtworkDataForm(this.artworkDataPartTemplate);
    this.removeSingleThumbnailContainerFromPersonalDataForm(this.formBody);

    this.appendArtworkDataSection();
    this.formGroup.addControl(PERSONAL_DATA_MEDIA_FORM_FIELD, new FormArray([]));
    this.rewriteValidators(PERSONAL_DATA_FILES_FORM_FIELD, PERSONAL_DATA_MEDIA_FORM_FIELD);

    this._fs.changed.subscribe(value => {
      if (value.formControlName === PERSONAL_DATA_FILES_FORM_FIELD) {
        this.handleFilesChangePersonalData(value.event.target.files);
      } else if (value.formControlName.endsWith(ARTWORK_DATA_FILE_INPUT_FORM_FIELD_SUFFIX)) {
        this.handleFilesChange(value.formControlName, value.event.target.files);
      }
    });

    // nasłuchiwanie na akcje z ButtonHandlerService
    this.subSink.sink = this._buttonHandlerService.click$.subscribe((action: BlockAction) => {
      switch (action.actionType) {
        case BlockActionType.SUBMIT:
          if (this.isSubmitDisabled) {
            return;
          }
          this.onSubmitClick(action); // kliknięcie "Wyślij"
          break;
        case BlockActionType.CANCEL:
          this.onCancelClick(); // kliknięcie "Anuluj"
          break;
        case BlockActionType.ADD_SECTION:
          this.onAddSectionClick(); // kliknięcie "Dodaj sekcję"
          break;
        case BlockActionType.REMOVE_SECTION:
          this.onRemoveSectionClick(action.actionData.sectionIndex); // kliknięcie "Usuń sekcję"
          break;
        case BlockActionType.CLEAR:
          this.onRemoveUploadedFile(action); // kliknięcie "Usuń plik"
          break;
        default:
          void 0;
      }
    });
  }

  private skipRemovedSection(obj: MixedObject) {
    obj.artworksData = obj.artworksData.filter((v, idx) => !this.removedIndexes.includes(idx));
    return obj;
  }

  private getCleanMediaIds(obj: MixedObject) {
    obj.artworksData = obj.artworksData.map(data => {
      data.media_ids = data.media_ids.map(o => o.id);
      return data;
    });
    obj.personalData.media_ids = obj.personalData.media_ids.map(o => o.id);
    return obj;
  }

  private appendArtworkDataSection() {
    if (this.maxArtworksCountReached) {
      return; // ograniczenie do max 10 sekcji
    }
    // klonuje szablon i nadaje mu nowy indeks
    const oldControlName = `${ARTWORK_DATA_FORM_FIELD_PREFIX}:${this.allArtworksDataLength}:main_form_files`;
    const newControlName = `${ARTWORK_DATA_FORM_FIELD_PREFIX}:${this.allArtworksDataLength}:media_ids`;
    this.formGroup.addControl(newControlName, new FormArray([]));
    this.artworkDataPartArray.push(this.artworkDataPartMapper(this.artworkDataPartTemplate.clone()));
    if (this.maxArtworksCountReached) {
      this.hideAddSectionBlock();
    }
    setTimeout(() => {
      this.rewriteValidators(
        oldControlName,
        newControlName,
      );
    }, 20);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  // wysyła dane formularza na backend
  private async sendMessage(): Promise<boolean> {
    this.formGroup.setErrors({'status': true});
    return new Promise((resolve, reject) => {
      const rawObj = this._fs.formGroup.getRawValue(); // pobiera dane płaskie
      const validFormObj = this.getCleanMediaIds( // konwersja {id:int;full_url:string} na int[]
        this.skipRemovedSection(nestObject(rawObj)),  // zamienia dane na obiekt zagnieżdżony
      );

      this.artworkRequestFormService.sendMessage(validFormObj, true, this.specialBlock.id)
        .pipe(
          catchError(({ok, error}) => {
            Object.keys(error.errors).forEach(key => {
              const finalKey = key.replaceAll('.', ':').replace('artworks_data', 'artworksData').replace('personal_data', 'personalData');
              const control = this.formGroup.get(finalKey);
              if (control) {
                control.setErrors({
                  customError: {
                    message: error.errors[key].join(' '),
                  },
                });
              }
            });
            return of(ok);
          }),
        ).subscribe({
        next: res => resolve(!!res),
        error: err => {
          console.log('err', err.error);
          reject(false);
        },
      });
    });
  }

  private findAndAssignTemplateParts(blk: Block) {
    // szuka pierwszej sekcji typu artworkData i zapisuje ją jako szablon
    for (const child of blk.children) {
      if (isArtworkDataPart(child) && !this.artworkDataPartTemplate) {
        this.artworkDataPartTemplate = child;
      }
      if (isArtworksDataUploadSingleThumbnailContainer(child) && !this.singleThumbnailPartTemplate) {
        this.singleThumbnailPartTemplate = child;
      }
      if (isPersonalDataUploadSingleThumbnailContainer(child) && !this.singleThumbnailPartPersonalDataTemplate) {
        this.singleThumbnailPartPersonalDataTemplate = child;
      }

      // jak juz mamy przypisane co trzeba to dalej nie szukamy
      if (this.artworkDataPartTemplate && this.singleThumbnailPartTemplate && this.singleThumbnailPartPersonalDataTemplate) {
        break;
      }

      this.findAndAssignTemplateParts(child);
    }
  }

  private removeSingleThumbnailContainerFromArtworkDataForm(blk: Block) {
    // wstepnie oprozniamy kontener miniatur bo na poczatku nic nie jest dodane
    // potem bedziemy je doklejac do tego kontenera
    for (const child of blk.children) {
      if (isArtworksDataUploadThumbnailsContainer(child)) {
        child.children = [];
        break;
      }

      this.removeSingleThumbnailContainerFromArtworkDataForm(child);
    }
  }

  private removeSingleThumbnailContainerFromPersonalDataForm(blk: Block) {
    // wstepnie oprozniamy kontener miniatur bo na poczatku nic nie jest dodane
    // potem bedziemy je doklejac do tego kontenera
    for (const child of blk.children) {
      if (isPersonalDataUploadThumbnailsContainer(child)) {
        child.children = [];
        break;
      }

      this.removeSingleThumbnailContainerFromPersonalDataForm(child);
    }
  }

  // klonuje blok sekcji artworkData i podmienia indeksy w nazwach pól
  private artworkDataPartMapper(blk: Block): Block {
    const artworkDataPartArrayLength = this.artworkDataPartArray.length;
    if (blk.settings.formControlName) {
      blk.settings.formControlName = blk.settings.formControlName.split(':').map(val => {
        if (isNaN(+val)) {
          return val;
        }
        return artworkDataPartArrayLength;
      }).join(':');
    }

    // ustawia numer sekcji w treści bloku
    if (blk.content === ITEMS_COUNTER_SNIPPET) {
      blk.content = '';
      blk.additionalClasses.push('counter-class');
    }

    // ustawia indeks w akcji usuwania sekcji
    if (blk.action?.actionType === BlockActionType.REMOVE_SECTION) {
      blk.action.actionData.sectionIndex = artworkDataPartArrayLength;
    }

    // rekurencyjnie mapuje dzieci bloku
    blk.children = blk.children.map(c => this.artworkDataPartMapper(c));

    return blk;
  }

  // obsługa kliknięcia "Wyślij"
  private async onSubmitClick(action: BlockAction): Promise<void> {
    if (this.isSendingInProgress || this.mediaSendingInProgressCount > 0) {
      return; // zapobiega wielokrotnej wysyłce oraz wysyłce podczas uploadu plików
    }
    this.setIsSendingInProgress(true);
    this.formGroup.markAllAsTouched();

    if (this.minArtworksCount > 0) {
      if (this.artworkDataPartArray.length < this.minArtworksCount) {
        this.setIsSendingInProgress(false);

        this._snackBar
          .openFromComponent(SnackBarMessageComponent, {
            data: this.getMinimumArtworksError(),
          })
          .afterDismissed()
          .subscribe();
        return;
      }
    }

    this.removeValidationInfoUnderForm();
    if (!this.formGroup.valid) {
      if (this.findFormErrors()) {
        this.setIsSendingInProgress(false);
        this.addValidationInfoUnderForm();
        return; // nie wysyła jeśli formularz niepoprawny
      }
    }
    const reCaptchaResult = await this.apiReCaptchaService.verifyRecaptchaIfNeeded();
    // jeśli reCaptcha wykryje robota, przerywamy wysyłkę
    if (reCaptchaResult === false) {
      this.setIsSendingInProgress(false);
      return;
    }

    const sendMsgRes = await this.sendMessage();
    if (sendMsgRes) {
      // jeśli formularz jest w pop-upie, zamyka go po wysyłce
      if (this.element.nativeElement.closest('app-popup')) {
        this._buttonHandlerService.click$.next({
          ...action,
          actionType: BlockActionType.CLOSE,
        });
      }
      // symulujemy ręczne klikanie usunięcia sekcji
      // tak jest prościej niż głęboko po tablicach kombinować i szukać indeksów.
      Array.from(document.querySelectorAll('[action-name="REMOVE_SECTION"]'))
        .reverse()
        .forEach((el: HTMLElement) => el.click());
      // symulujemy ręczne klikanie usunięcia plików
      // tak jest prościej niż głęboko po tablicach kombinować i szukać indeksów.
      Array.from(document.querySelectorAll('[action-name="CLEAR"]'))
        .reverse()
        .forEach((el: HTMLElement) => el.click());
      this.onAddSectionClick();
      this.formGroup.reset();
    } else {
      this.findFormErrors();
    }
    this.setIsSendingInProgress(false);
    this._snackBar
      .openFromComponent(SnackBarMessageComponent, {
        data: sendMsgRes ? this.getSuccessMsg() : this.getErrorMessage(),
      })
      .afterDismissed()
      .subscribe();
  }

  private findFormErrors() {
    const formErrors = [];
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      if (!control?.errors) {
        if (key.endsWith('media_ids')) {
          const parentControlName = key.replace(':media_ids', ':main_form_files');
          const parentControl = this.formGroup.get(parentControlName);
          if (parentControl) {
            parentControl.setErrors(null);
          }
        }
        return;
      }
      if (key.startsWith('artworksData')) {
        const [, sectionIndex] = key.split(':');
        if (this.removedIndexes.includes(+sectionIndex)) {
          return;
        }
      }
      if (key.endsWith('media_ids')) {
        const parentControlName = key.replace(':media_ids', ':main_form_files');
        const parentControl = this.formGroup.get(parentControlName);
        if (parentControl) {
          parentControl.setErrors(control.errors);
        }
        formErrors.push({parentControlName, errors: control.errors});
      } else {
        formErrors.push({key, errors: control.errors});
      }
    });
    console.log('formErrors', formErrors);
    if (formErrors.length) {
      setTimeout(() => {
        document.querySelectorAll('.mat-form-field-invalid').forEach(el => el.scrollIntoView({behavior: 'smooth'}));
      }, 10);
      return true;
    }
    return false;
  }

  private getSuccessMsg(): string {
    const locale = this.storageService.getItem('language');
    const msgList = {
      pl: 'Dziękujemy za kontakt.\nSkontaktujemy się z Tobą tak szybko, jak to tylko możliwe',
      en: 'Thank you for contacting us.\nWe will get back to you as soon as possible.',
      de: 'Vielen Dank für Ihre Kontaktaufnahme.\nWir werden uns so schnell wie möglich mit Ihnen in Verbindung setzen.',
      fr: 'Merci de nous avoir contactés.\nNous vous répondrons dans les plus brefs délais.',
      es: 'Gracias por contactarnos.\nNos pondremos en contacto contigo lo antes posible.',
      ru: 'Спасибо за обращение.\nМы свяжемся с вами в кратчайшие сроки.',
    };
    return msgList[locale] || 'contactForm.success';
  }

  private getErrorMessage(): string {
    const locale = this.storageService.getItem('language');
    const msgList = {
      pl: 'Przepraszamy, coś poszło nie tak.\nSpróbuj wysłać wiadomość jeszcze raz bądź skontaktuj się z nami telefonicznie.',
      en: 'Sorry, something went wrong.\nPlease try sending the message again or contact us by phone.',
      fr: 'Désolé, quelque chose s\'est mal passé.\nVeuillez réessayer d\'envoyer le message ou contactez-nous par téléphone.',
      de: 'Entschuldigung, es ist ein Fehler aufgetreten.\nBitte versuchen Sie, die Nachricht erneut zu senden oder kontaktieren Sie uns telefonisch.',
      es: 'Lo sentimos, algo salió mal.\nIntenta enviar el mensaje de nuevo o ponte en contacto con nosotros por teléfono.',
      ru: 'Извините, что-то пошло не так.\nПопробуйте отправить сообщение ещё раз или свяжитесь с нами по телефону.',
    };
    return msgList[locale] || 'contactForm.error';
  }

  private getMinimumArtworksError(): string {
    const minArtworks = this.minArtworksCount;
    const locale = this.storageService.getItem('language');
    const msgList = {
      pl: `Zgłoszenie musi zawierać co najmniej ${minArtworks} prace.`,
      en: `The submission must contain at least ${minArtworks} works.`,
      fr: `La soumission doit contenir au moins ${minArtworks} œuvres.`,
      de: `Die Einreichung muss mindestens ${minArtworks} Werke enthalten.`,
      es: `La presentación debe contener al menos ${minArtworks} obras.`,
      ru: `Заявка должна содержать не менее ${minArtworks} работ.`,
    };
    return msgList[locale] || 'contactForm.error';
  }

  // obsługa kliknięcia "Anuluj"
  private onCancelClick(): void {
    this.formGroup.reset();
  }

  // obsługa kliknięcia "Dodaj sekcję"
  private onAddSectionClick(): void {
    this.appendArtworkDataSection();
  }

  // obsługa kliknięcia "Usuń sekcję"
  private onRemoveSectionClick(sectionIndex: number): void {
    this.removedIndexes.push(sectionIndex);
    this.showAddSectionBlock();
  }

  private hideFileInput(sectionIndex: string | number) {
    this.findFileInputBlock(sectionIndex)?.setAttribute('hidden', 'true');
  }

  private showFileInput(sectionIndex: string | number) {
    this.findFileInputBlock(sectionIndex)?.removeAttribute('hidden');
  }

  private findFileInputBlock(sectionIndex: string | number) {
    return document.querySelector(`.artwork-data-form.idx-${sectionIndex} input[type="file"]`)?.closest('[app-block]');
  }

  private hideFileInputInPersonalData() {
    this.findFileInputBlockInPersonalData()?.setAttribute('hidden', 'true');
  }

  private showFileInputInPersonalData() {
    this.findFileInputBlockInPersonalData()?.removeAttribute('hidden');
  }

  private findFileInputBlockInPersonalData() {
    return document.querySelector(`.personal-data-form input[type="file"]`)?.closest('[app-block]');
  }

  private findAddSectionBlock() {
    return document.querySelector(`.${this.specialBlock.uId} [action-name="ADD_SECTION"]`);
  }

  /** Host klikalny przycisku Wyślij (ClickableBlockDirective). */
  private findSubmitButton(): HTMLElement | null {
    return document.querySelector(`.${this.specialBlock.uId} [action-name="SUBMIT"]`);
  }

  /** Etykieta / .ql-editor wewnątrz przycisku — do loadera przy wysyłce. */
  private findSubmitButtonLabel(): HTMLElement | null {
    return document.querySelector(`.${this.specialBlock.uId} [action-name="SUBMIT"] .ql-editor`);
  }

  private addValidationInfoUnderForm() {
    const form = this.element.nativeElement.querySelector('form');
    if (form) {
      const lang = this.storageService.getItem('language');
      const message = ARTWORK_REQUEST_ERRORS.requiredFieldsErr[lang];
      const div = document.createElement('div');
      div.classList.add('validation-info');
      div.textContent = message;
      form.append(div);
    }
  }

  private removeValidationInfoUnderForm() {
    const form = this.element.nativeElement.querySelector('form');
    if (form) {
      const validationInfo = form.querySelector('.validation-info');
      if (validationInfo) {
        validationInfo.remove();
      }
    }
  }

  private replaceSubmitBtnTextWithLoader(): void {
    const submitLabel = this.findSubmitButtonLabel();
    if (!submitLabel) {
      return;
    }
    if (!this.submitBtnText) {
      this.submitBtnText = submitLabel.textContent;
    }
    const height = submitLabel.clientHeight;
    const div = document.createElement('span');
    div.classList.add('loader-container');
    div.innerHTML = this.loaderHtml;
    const svg = div.querySelector('svg');
    svg.style.width = `${height}`;
    svg.style.height = `${height}`;
    const circle = svg.querySelector('circle');
    circle.setAttribute('cx', `${height / 2}`);
    circle.setAttribute('cy', `${height / 2}`);
    const strokeWidth = +circle.getAttribute('stroke-width');
    circle.setAttribute('r', `${(height / 2) - (strokeWidth / 2)}`);

    submitLabel.innerHTML = div.outerHTML;
  }

  private setSubmitBtnDisableStatus(value: boolean): void {
    // Zawsze — ClickableBlockDirective nie honoruje native disabled; guard w click$.
    this.isSubmitDisabled = value;

    const submitBtn = this.findSubmitButton();
    if (!submitBtn) {
      return;
    }

    if (value) {
      submitBtn.setAttribute('disabled', 'disabled');
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-disabled');
      // Inline !important — style szablonu bloku często nadpisują opacity z CSS.
      submitBtn.style.setProperty('opacity', '0.45', 'important');
      submitBtn.style.setProperty('filter', 'grayscale(0.4) brightness(0.65)', 'important');
      submitBtn.style.setProperty('pointer-events', 'none', 'important');
      submitBtn.style.setProperty('cursor', 'not-allowed', 'important');
    } else {
      submitBtn.removeAttribute('disabled');
      submitBtn.setAttribute('aria-disabled', 'false');
      submitBtn.classList.remove('is-disabled');
      submitBtn.style.removeProperty('opacity');
      submitBtn.style.removeProperty('filter');
      submitBtn.style.removeProperty('pointer-events');
      submitBtn.style.removeProperty('cursor');
    }
  }

  private replaceSubmitBtnLoaderWithText(): void {
    const submitLabel = this.findSubmitButtonLabel();
    if (submitLabel) {
      // AT-9147: zapamiętana etykieta pochodzi z `textContent`, więc przywracamy ją jako tekst —
      // `innerHTML` zamieniłby etykietę zawierającą `<img onerror>` (widoczną jako tekst) w znacznik.
      submitLabel.textContent = this.submitBtnText;
    }
  }

  private showAddSectionBlock() {
    this.findAddSectionBlock()?.removeAttribute('hidden');
  }

  private hideAddSectionBlock() {
    this.findAddSectionBlock()?.setAttribute('hidden', 'hidden');
  }

  private handleFilesChangePersonalData(files: FileList) {
    const mediaControl = (this.formGroup.get(PERSONAL_DATA_MEDIA_FORM_FIELD) as FormArray);
    let currentSectionMediaLength = mediaControl.value.length;
    for (let i = 0; i < files.length; i++) {
      // ograniczenie do 5 plikow per praca
      if (currentSectionMediaLength >= 5) {
        this.hideFileInputInPersonalData();
        break;
      }
      this.mediaSendingInProgressCount++;
      const file = files.item(i); // albo files[i]
      const fileType = file.type.split('/')[0];

      if (!this.validateFileSize(file)) {
        this.mediaSendingInProgressCount--;
        this.showFileTooLargeInfo(fileType);
        continue;
      }

      const fileThumbnail = this.appendThumbnailBlockToPersonalData(this.formBody, currentSectionMediaLength);
      const progressbar = this.resetProgressbar(fileThumbnail);
      currentSectionMediaLength++;
      const formGroup = this.getEmptyFormGroupForFile();
      mediaControl.push(formGroup);

      fileToBase64(file).then(base64 => {
        if (currentSectionMediaLength >= 5) {
          this.hideFileInputInPersonalData();
        }

        this.updateThumbnailImage(fileThumbnail, this.getPrototypedMedia(file, base64));

        const onErr = (err: any) => {
          this.mediaSendingInProgressCount--;
          this.regenerateThumbnailsInPersonalData();
          currentSectionMediaLength--;
          const index = mediaControl.controls.indexOf(formGroup);
          if (index !== -1) {
            mediaControl.removeAt(index);
          }
          if (mediaControl.length < 5) {
            this.showFileInputInPersonalData();
          }
          this.onErrorUploadFile(err, fileThumbnail, fileType);
        };

        this.artworkRequestFormService.uploadAttachment(file, percent => {
          progressbar.styles.all.basic.width = `${percent}%`;
        })
          .pipe(catchError(err => {
            onErr(err);
            return EMPTY; // bez of(null) — inaczej next robi drugi --
          }))
          .subscribe({
            next: result => {
              this.mediaSendingInProgressCount--;
              if (!result?.id) {
                return;
              }
              this.onSuccessUploadFile(result, fileThumbnail, formGroup, base64);
              if (mediaControl.length >= 5) {
                this.hideFileInputInPersonalData();
              }
            },
          });
      });
    }

    this.formGroup.get(PERSONAL_DATA_FILES_FORM_FIELD).reset();
  }

  private handleFilesChange(path: string, files: FileList) {
    const [, indexOfFormSection] = path.split(':');
    const mediaControl = (this.formGroup.get(getMediaFieldName(indexOfFormSection)) as FormArray);
    let currentSectionMediaLength = mediaControl.value.length;
    for (let i = 0; i < files.length; i++) {
      // ograniczenie do 5 plikow per praca
      if (currentSectionMediaLength >= 5) {
        this.hideFileInput(indexOfFormSection);
        break;
      }
      this.mediaSendingInProgressCount++;
      const file = files.item(i); // albo files[i]
      const fileType = file.type.split('/')[0];
      if (!this.validateFileSize(file)) {
        this.mediaSendingInProgressCount--;
        this.showFileTooLargeInfo(fileType);
        continue;
      }

      const fileThumbnail = this.appendThumbnailBlock(+indexOfFormSection, this.artworkDataPartArray[indexOfFormSection], currentSectionMediaLength);
      const progressbar = this.resetProgressbar(fileThumbnail);
      currentSectionMediaLength++;
      const formGroup = this.getEmptyFormGroupForFile();
      mediaControl.push(formGroup);

      fileToBase64(file).then(base64 => {
        if (currentSectionMediaLength >= 5) {
          this.hideFileInput(indexOfFormSection);
        }
        this.updateThumbnailImage(fileThumbnail, this.getPrototypedMedia(file, base64));

        const onErr = (err: any) => {
          this.mediaSendingInProgressCount--;
          this.regenerateThumbnails(indexOfFormSection);
          currentSectionMediaLength--;
          const index = mediaControl.controls.indexOf(formGroup);
          if (index !== -1) {
            mediaControl.removeAt(index);
          }
          if (mediaControl.length < 5) {
            this.showFileInput(indexOfFormSection);
          }
          this.onErrorUploadFile(err, fileThumbnail, fileType);
        };


        this.artworkRequestFormService.uploadImage(file, percent => {
          progressbar.styles.all.basic.width = `${percent}%`;
        })
          .pipe(catchError(err => {
            onErr(err);
            return EMPTY; // bez of(null) — inaczej next robi drugi --
          }))
          .subscribe({
            next: result => {
              this.mediaSendingInProgressCount--;
              if (!result?.id) {
                return;
              }
              this.onSuccessUploadFile(result, fileThumbnail, formGroup, base64);
              if (mediaControl.length >= 5) {
                this.hideFileInput(indexOfFormSection);
              }
            },
          });
      });
    }

    this.formGroup.get(path).reset();
  }

  private validateFileSize(file: File) {
    const fileType = file.type.split('/')[0];
    if (fileType === 'video') {
      return file.size <= this.maxVideoFileSize;
    }
    return file.size <= this.maxFileSize;

  }

  private resetProgressbar(fileThumbnail: Block) {
    const progressbar = this.findProgressbar(fileThumbnail);
    progressbar.settings.viewPart = ViewPart.PROGRESS;
    progressbar.styles.all.basic.width = '0%';
    return progressbar;
  }

  private getEmptyFormGroupForFile() {
    return new FormGroup({
      id: new FormControl(null),
      fullUrl: new FormControl(null),
      type: new FormControl(null),
      extension: new FormControl(null),
    });
  }

  private getPrototypedMedia(file: File, base64: string): MediaInterface {
    return {
      fullUrl: base64,
      type: file.type,
      extension: file.name.split('.').at(-1),
      urls: {
        full: base64,
        thumbnails: {
          mini300: base64,
          mini600: base64,
          mini800: base64,
          mini1200: base64,
          mini1500: base64,
          mini2000: base64,
        },
      },
    } as MediaInterface;
  }

  private onSuccessUploadFile(result: any, fileThumbnail: Block, formGroup: FormGroup, base64: string) {
    this.removeProgressbar(fileThumbnail);
    const {type, extension} = result;
    this.updateThumbnailImage(fileThumbnail, {fullUrl: base64, type, extension, id: result.id} as MediaInterface);
    formGroup.patchValue({id: result.id, fullUrl: base64, type, extension});
  }

  private onErrorUploadFile(err: any, fileThumbnail: Block, fileType: string) {
    this.removeProgressbar(fileThumbnail);
    if (
      err.error?.artsaas_code === ArtsaasErrorCode.MEDIA_FILE_TOO_LARGE
      || err.status === 413
    ) {
      this.showFileTooLargeInfo(fileType);
    }
    console.log('error', err.error);
  }

  private showFileTooLargeInfo(fileType: string) {
    const lang = this.storageService.getItem('language');
    let errorMsg: string;
    if (fileType === 'image') {
      errorMsg = ARTWORK_REQUEST_ERRORS.imageSizeErr[lang];
    } else if (fileType === 'video') {
      errorMsg = ARTWORK_REQUEST_ERRORS.videoSizeErr[lang];
    } else {
      errorMsg = ARTWORK_REQUEST_ERRORS.fileSizeErr[lang];
    }
    this._snackBar.openFromComponent(SnackBarMessageComponent, {
      data: errorMsg,
    }).afterDismissed().subscribe();
  }

  private updateThumbnailImage(thumbnailBlock: Block, img: MediaInterface) {
    if (img.id) {
      thumbnailBlock.additionalClasses = thumbnailBlock.additionalClasses.filter(c => c !== 'hide-close-btn');
      const selector = [...thumbnailBlock.additionalClasses, thumbnailBlock.uId].map(c => `.${c}`).join('');
      document.querySelector(selector)?.classList.remove('hide-close-btn');
    }
    thumbnailBlock.children = thumbnailBlock.children.map(c => {
      if (c.content === THUMBNAIL_SNIPPET) {
        if (img.type.startsWith('application')) {
          c.content = `<p style="
            font-size: 36px;
            background: #f54d46;
            height: 100%;
            place-items: center;
            display: grid;
          ">.${img.extension}</p>`;
          c.node = BlockNodeEnum.PARAGRAPH_NODE;
        } else {
          if (!c.image) {
            c.image = img;
          } else {
            c.image.fullUrl = img.fullUrl;
          }
          c.image.urls = {full: img.fullUrl, thumbnails: {frame1: img.fullUrl}} as any;
        }
      }
      this.updateThumbnailImage(c, img);
      return c;
    });
  }

  private findProgressbar(thumbnailBlock: Block): Block {
    for (const c of thumbnailBlock.children) {
      if (c.content === PROGRESSBAR_SNIPPET) {
        return c;
      }
      const found = this.findProgressbar(c);
      if (found) {
        return found;
      }
    }
  }

  private removeProgressbar(thumbnailBlock: Block) {
    for (const child of thumbnailBlock.children) {
      if (isArtworksDataUploadSingleThumbnailContainerWithProgress(child)
        || isPersonalDataUploadSingleThumbnailContainerWithProgress(child)) {
        child.children = child.children.filter(child => {
          if (child.node !== BlockNodeEnum.SNIPPET_NODE) {
            return true;
          }
          return child.content !== PROGRESSBAR_SNIPPET;
        });
        break;
      }

      this.removeSingleThumbnailContainerFromArtworkDataForm(child);
    }
  }

  private onRemoveUploadedFile(action: BlockAction) {
    const {thumbnailIndex, sectionIndex, part} = action.actionData;
    let mediaFormArr: FormArray;
    if (part === 'personalData') {
      this.showFileInputInPersonalData();
      mediaFormArr = this.formGroup.get(PERSONAL_DATA_MEDIA_FORM_FIELD) as FormArray;
    } else {
      this.showFileInput(sectionIndex);
      mediaFormArr = this.formGroup.get(getMediaFieldName(sectionIndex)) as FormArray;
    }
    const mediaIds = mediaFormArr.value;
    if (!mediaIds.length) {
      return;
    }
    // usuwamy N-ty blok w kontenerze
    mediaIds.splice(thumbnailIndex, 1);

    if (part === 'personalData') {
      this.regenerateThumbnailsInPersonalData();
    } else {
      this.regenerateThumbnails(sectionIndex);
    }
    mediaFormArr.removeAt(thumbnailIndex);
  }

  private regenerateThumbnails(sectionIndex: number | string) {
    const mediaFormArr = this.formGroup.get(getMediaFieldName(sectionIndex)) as FormArray;
    const mediaIds = mediaFormArr.value;
    this.removeSingleThumbnailContainerFromArtworkDataForm(this.artworkDataPartArray[sectionIndex]);
    mediaIds.forEach((media: MediaInterface, i: number) => {
      if (!media?.id) {
        return;
      }
      console.log('media', media);
      const fileThumbnail = this.appendThumbnailBlock(+sectionIndex, this.artworkDataPartArray[sectionIndex], i);
      this.removeProgressbar(fileThumbnail);
      this.updateThumbnailImage(fileThumbnail, media as MediaInterface);
    });
  }

  private regenerateThumbnailsInPersonalData() {
    const mediaFormArr = this.formGroup.get(PERSONAL_DATA_MEDIA_FORM_FIELD) as FormArray;
    const mediaIds = mediaFormArr.value;
    this.removeSingleThumbnailContainerFromPersonalDataForm(this.formBody);
    mediaIds.forEach((media: MediaInterface, i: number) => {
      if (!media?.id) {
        return;
      }
      const fileThumbnail = this.appendThumbnailBlockToPersonalData(this.formBody, i);
      this.removeProgressbar(fileThumbnail);
      this.updateThumbnailImage(fileThumbnail, media as MediaInterface);
    });
  }

  private appendThumbnailBlock(indexOfFormSection: number, blk: Block, idx: number): Block | undefined {
    let newBlock: Block;
    for (const child of blk.children) {
      if (isArtworksDataUploadThumbnailsContainer(child)) {
        newBlock = this.singleThumbnailPartTemplate.clone();
        newBlock.additionalClasses.push(`thumbnail-${indexOfFormSection}-${idx}`, 'hide-close-btn');
        newBlock.children = newBlock.children.map(c => {
          if (c.action.actionType === BlockActionType.CLEAR) {
            c.action.actionData.thumbnailIndex = idx;
            c.action.actionData.sectionIndex = indexOfFormSection;
          }
          return c;
        });
        child.children.push(newBlock);
        return newBlock;
      }

      const blockAdded = this.appendThumbnailBlock(indexOfFormSection, child, idx);
      if (blockAdded) {
        return blockAdded;
      }
    }
  }

  private appendThumbnailBlockToPersonalData(blk: Block, idx: number): Block | undefined {
    let newBlock: Block;
    for (const child of blk.children) {
      if (isPersonalDataUploadThumbnailsContainer(child)) {
        newBlock = this.singleThumbnailPartPersonalDataTemplate.clone();
        newBlock.additionalClasses.push(`thumbnail-${idx}`, 'hide-close-btn');
        newBlock.children = newBlock.children.map(c => {
          if (c.action.actionType === BlockActionType.CLEAR) {
            c.action.actionData.thumbnailIndex = idx;
          }
          return c;
        });
        child.children.push(newBlock);
        return newBlock;
      }

      const blockAdded = this.appendThumbnailBlockToPersonalData(child, idx);
      if (blockAdded) {
        return blockAdded;
      }
    }
  }

  private rewriteValidators(sourceControlName: string, targetControlName: string) {
    const sourceControl = this.formGroup.get(sourceControlName) as FormControl;
    const targetControl = this.formGroup.get(targetControlName) as FormControl;

    // Pobranie walidatorów
    const syncValidators = sourceControl.validator;
    const asyncValidators = sourceControl.asyncValidator;

    // Przepisanie do nowego controla
    targetControl.setValidators(syncValidators);
    targetControl.setAsyncValidators(asyncValidators);

    // Aktualizacja nowego controla
    targetControl.updateValueAndValidity();

    // Usunięcie z poprzedniego controla
    sourceControl.clearValidators();
    sourceControl.clearAsyncValidators();

    // Aktualizacja starego controla
    sourceControl.updateValueAndValidity();

  }
}
